import axios from 'axios';
import { decodeJwt, isExpiredJwt } from 'src/utils/parseJwtUtils';
import { readJson } from 'src/utils/safeStorage';
import { AUTH_TOKEN_STORAGE_KEY, AUTH_USER_STORAGE_KEY, isStoredUser } from '../slices/authSlice.keys';

/**
 * Session handling that is shared by all requests and all tabs.
 *
 * - A 401 triggers ONE re-check (POST /auth/refresh with the token that
 *   failed). All concurrent 401s for the same token await the same promise.
 * - Only a 401 on the re-check logs out, and only when the stored token is
 *   still the one that failed. A newer token (other tab) is used instead.
 * - Past half of its lifetime, an active session refreshes once. A short
 *   localStorage lock and a re-read of the stored token keep other tabs from
 *   refreshing at the same time.
 */

export type SessionUser = unknown;
export type Session = { token: string; user: SessionUser };

export type RecheckResult = { kind: 'retry'; token: string } | { kind: 'logout' } | { kind: 'unknown' };

export const REFRESH_LOCK_KEY = 'token-refresh-lock';
const LOCK_TTL_MS = 15_000;
const REFRESH_PATH = '/api/v1/auth/refresh';

export const readStoredToken = (): string => localStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || '';

export type SessionHooks = {
  baseUrl: string;
  /** Token the app currently holds in memory. */
  getToken: () => string;
  /** Adopt a session (new token) in memory and storage. */
  adopt: (session: Session) => void;
  /** Drop the session in memory and storage. */
  logout: () => void;
};

let recheckInFlight: { token: string; promise: Promise<RecheckResult> } | null = null;
let refreshInFlight: Promise<void> | null = null;
/** Tokens the server refused to refresh with 403 (demo): do not retry. */
const notRefreshable = new Set<string>();

export const resetSessionState = () => {
  recheckInFlight = null;
  refreshInFlight = null;
  notRefreshable.clear();
};

type RefreshOutcome = { status: 'ok'; session: Session } | { status: 'invalid' } | { status: 'denied' } | { status: 'failed' };

const callRefresh = async (baseUrl: string, token: string): Promise<RefreshOutcome> => {
  try {
    const response = await axios.post(baseUrl + REFRESH_PATH, undefined, { headers: { authorization: `Bearer ${token}` } });
    const { token: fresh, user } = response.data || {};
    if (typeof fresh !== 'string' || !fresh) return { status: 'failed' };
    return { status: 'ok', session: { token: fresh, user } };
  } catch (error) {
    const status = (error as { response?: { status?: number } }).response?.status;
    if (status === 401) return { status: 'invalid' };
    // 403: authenticated but not refreshable (demo). Network/5xx: unknown.
    if (status === 403) return { status: 'denied' };
    return { status: 'failed' };
  }
};

/**
 * Called after a request got 401 with `failedToken`. Never throws.
 */
export const recheckSession = (failedToken: string, hooks: SessionHooks): Promise<RecheckResult> => {
  if (recheckInFlight && recheckInFlight.token === failedToken) {
    return recheckInFlight.promise;
  }
  const promise = (async (): Promise<RecheckResult> => {
    // Another tab may have logged in or refreshed already.
    const stored = readStoredToken();
    if (stored && stored !== failedToken) {
      if (isExpiredJwt(stored)) return finishLogout(stored, hooks);
      hooks.getToken() !== stored && adoptStored(hooks);
      return { kind: 'retry', token: stored };
    }
    if (!stored) return finishLogout(failedToken, hooks);
    if (isExpiredJwt(failedToken)) return finishLogout(failedToken, hooks);

    const outcome = await callRefresh(hooks.baseUrl, failedToken);
    switch (outcome.status) {
      case 'ok':
        // A newer token written meanwhile wins over ours.
        if (readStoredToken() !== failedToken) {
          const newer = readStoredToken();
          return newer && !isExpiredJwt(newer) ? { kind: 'retry', token: newer } : finishLogout(newer, hooks);
        }
        hooks.adopt(outcome.session);
        return { kind: 'retry', token: outcome.session.token };
      case 'denied':
        // The token is valid (403); the first 401 was a blip.
        notRefreshable.add(failedToken);
        return { kind: 'retry', token: failedToken };
      case 'invalid':
        return finishLogout(failedToken, hooks);
      default:
        return { kind: 'unknown' };
    }
  })().finally(() => {
    if (recheckInFlight?.promise === promise) recheckInFlight = null;
  });
  recheckInFlight = { token: failedToken, promise };
  return promise;
};

const adoptStored = (hooks: SessionHooks) => {
  const token = readStoredToken();
  // An unreadable user (or one without roleNames) is not adopted: memory state stays.
  const user = readJson<unknown>(AUTH_USER_STORAGE_KEY, null, isStoredUser);
  if (!token || !user) return;
  try {
    hooks.adopt({ token, user });
  } catch {
    /* keep memory state */
  }
};

const finishLogout = (failedToken: string, hooks: SessionHooks): RecheckResult => {
  // Never wipe a newer token written by another tab.
  const stored = readStoredToken();
  if (stored && stored !== failedToken && !isExpiredJwt(stored)) {
    return { kind: 'retry', token: stored };
  }
  hooks.logout();
  return { kind: 'logout' };
};

const lifetimeLeft = (token: string) => {
  const jwt = decodeJwt(token);
  if (!jwt || typeof jwt.exp !== 'number' || typeof jwt.iat !== 'number') {
    return null;
  }
  const now = Date.now() / 1000;
  return { remaining: jwt.exp - now, total: jwt.exp - jwt.iat };
};

export const isRefreshDue = (token: string) => {
  const info = lifetimeLeft(token);
  if (!info || info.total <= 0) return false;
  return info.remaining > 0 && info.remaining < info.total / 2;
};

const lockHeldByOtherTab = () => {
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  return !!raw && Date.now() - Number(raw) < LOCK_TTL_MS;
};

/**
 * Call on user activity. Refreshes at most once per half-life, never an
 * expired token, never when idle (the caller only invokes it on activity).
 */
export const refreshIfDue = (hooks: SessionHooks): Promise<void> => {
  if (refreshInFlight) return refreshInFlight;
  // Use the stored token: another tab may have refreshed already.
  const token = readStoredToken() || hooks.getToken();
  if (!token || notRefreshable.has(token) || !isRefreshDue(token)) {
    return Promise.resolve();
  }
  if (lockHeldByOtherTab()) return Promise.resolve();
  localStorage.setItem(REFRESH_LOCK_KEY, String(Date.now()));
  const promise = (async () => {
    try {
      const outcome = await callRefresh(hooks.baseUrl, token);
      if (outcome.status === 'ok') {
        if (readStoredToken() === token) hooks.adopt(outcome.session);
      } else if (outcome.status === 'denied') {
        notRefreshable.add(token);
      }
      // 'invalid' is left to the next real request's 401 re-check.
    } finally {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  })().finally(() => {
    refreshInFlight = null;
  });
  refreshInFlight = promise;
  return promise;
};
